import React, { useState, useEffect } from "react";
import {
  GoogleMap,
  LoadScript,
  Autocomplete,
  Marker,
} from "@react-google-maps/api";
import { Typography, TextField, Box, FormControl } from "@mui/material";
import {
  buildEmployeeAddress,
  validateEmployeeAddress,
} from "../../helpers/validateAddress";

const libraries = ["places"];

const FormDirections = ({
  values,
  errors,
  onChange,
  setValues,
  personSelected,
  deleteMode = false,
}) => {
  // new form
  const [autoComplete, setAutoComplete] = useState(null);
  const [location, setLocation] = useState(null);
  const [isValid, setIsValid] = useState(true);

  // callbacks
  const handleDragEnd = (event) => {
    setValues({
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    });
  };

  const handleAutoCompleteIsLoad = (inputAutoComplete) => {
    setAutoComplete(inputAutoComplete);
  };

  const handleAutoCompletePlaceIsChanged = () => {
    if (autoComplete !== null) {
      setLocation(autoComplete.getPlace());
    }
  };

  const success = (position) => {
    const currentPosition = {
      lat: parseFloat(position.coords.latitude),
      lng: parseFloat(position.coords.longitude),
    };
    setValues(currentPosition);
  };

  useEffect(() => {
    if (location?.address_components) {
      const newAddress = buildEmployeeAddress(location.address_components);
      setIsValid(validateEmployeeAddress(newAddress));
      setValues({
        ...newAddress,
        ...(location.geometry
          ? {
              lat: location.geometry.location.lat(),
              lng: location.geometry.location.lng(),
            }
          : {}),
      });
    }
  }, [location]);

  useEffect(() => {
    if (!personSelected) {
      navigator.geolocation.getCurrentPosition(success);
    }
  }, [personSelected]);

  useEffect(() => {
    if (personSelected) {
      if (personSelected.address) {
        setValues({
          id: personSelected.address?.id
            ? personSelected.address?.id
            : undefined,
          route: personSelected.address?.street
            ? personSelected.address?.street
            : "",
          street_number: personSelected.address?.ext_number
            ? personSelected.address?.ext_number
            : "",
          intNumber: personSelected.address?.int_number
            ? personSelected.address?.int_number
            : "",
          postal_code: personSelected.address?.postal_code
            ? personSelected.address?.postal_code
            : "",
          sublocality_level_1: personSelected.address?.colony
            ? personSelected.address?.colony
            : "",
          locality: personSelected.address?.municipality
            ? personSelected.address?.municipality
            : "",
          administrative_area_level_1: personSelected.address?.state
            ? personSelected.address?.state
            : "",
          reference: personSelected.address?.reference
            ? personSelected.address?.reference
            : "",
          lat: personSelected.address?.latitude
            ? personSelected.address?.latitude
            : 0,
          lng: personSelected.address?.longitude
            ? personSelected.address?.longitude
            : 0,
        });
      }
    }
  }, [personSelected]);

  return (
    <FormControl
      fullWidth
      style={{
        display: "flex",
        gap: 10,
        marginTop: 20,
      }}
    >
      <Typography component="h3">Dirección</Typography>
      <LoadScript
        libraries={libraries}
        googleMapsApiKey={process.env.REACT_APP_MAPS_API_KEY}
      >
        <Autocomplete
          onLoad={handleAutoCompleteIsLoad}
          restrictions={{
            country: ["mx"],
          }}
          onPlaceChanged={handleAutoCompletePlaceIsChanged}
          style={{ display: "block" }}
        >
          <TextField
            label="Buscar dirección completa"
            type="text"
            autoComplete="chrome-off"
            disabled={deleteMode}
            fullWidth
            variant="outlined"
            error={!isValid}
            helperText={
              isValid
                ? "Opcional: autocompleta los campos de abajo, que puedes editar a mano."
                : "No se pudo obtener la dirección completa, verifica los campos de abajo."
            }
          />
        </Autocomplete>
        <GoogleMap
          mapContainerClassName="form_customer_map"
          zoom={20}
          center={{
            lat: values.lat,
            lng: values.lng,
          }}
          options={{
            fullscreenControl: false,
          }}
        >
          <Marker
            draggable
            onDragEnd={handleDragEnd}
            position={{ lat: values.lat, lng: values.lng }}
          />
        </GoogleMap>
      </LoadScript>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          gap: 2,
          marginTop: 2,
          width: "100%",
        }}
      >
        <TextField
          label="Calle"
          type="text"
          autoComplete="chrome-off"
          name="route"
          value={values.route}
          disabled={deleteMode}
          onChange={onChange}
          variant="outlined"
          error={errors.route}
          helperText={errors.route}
        />
        <TextField
          label="Numero ext."
          type="text"
          autoComplete="chrome-off"
          name="street_number"
          value={values.street_number}
          disabled={deleteMode}
          onChange={onChange}
          variant="outlined"
          error={errors.street_number}
          helperText={errors.street_number}
        />
        <TextField
          label="Numero int."
          type="text"
          autoComplete="chrome-off"
          name="intNumber"
          value={values.intNumber}
          disabled={deleteMode}
          onChange={onChange}
          variant="outlined"
        />
        <TextField
          label="Codigo Postal"
          type="text"
          autoComplete="chrome-off"
          name="postal_code"
          value={values.postal_code}
          disabled={deleteMode}
          onChange={onChange}
          variant="outlined"
          error={errors.postal_code}
          helperText={errors.postal_code}
        />
        <TextField
          label="Colonia"
          type="text"
          autoComplete="chrome-off"
          name="sublocality_level_1"
          value={values.sublocality_level_1}
          disabled={deleteMode}
          onChange={onChange}
          variant="outlined"
          error={errors.sublocality_level_1}
          helperText={errors.sublocality_level_1}
        />
        <TextField
          label="Municipio"
          type="text"
          autoComplete="chrome-off"
          name="locality"
          value={values.locality}
          disabled={deleteMode}
          onChange={onChange}
          variant="outlined"
          error={errors.locality}
          helperText={errors.locality}
        />
        <TextField
          label="Estado"
          type="text"
          autoComplete="chrome-off"
          name="administrative_area_level_1"
          value={values.administrative_area_level_1}
          disabled={deleteMode}
          onChange={onChange}
          variant="outlined"
          error={errors.administrative_area_level_1}
          helperText={errors.administrative_area_level_1}
          sx={{ gridColumn: { sm: "1 / -1" } }}
        />
        <TextField
          label="Referencia"
          type="text"
          autoComplete="chrome-off"
          name="reference"
          value={values.reference}
          disabled={deleteMode}
          onChange={onChange}
          variant="outlined"
          multiline
          minRows={3}
          sx={{ gridColumn: { sm: "1 / -1" } }}
        />
      </Box>
    </FormControl>
  );
};

export default FormDirections;
